import type { FetchContext, FetchHook, FetchResponse } from 'ofetch'
import {
  API_SUCCESS_CODES,
  ApiError,
  FUND_PASSWORD_LOCKED_CODE,
  isApiUnauthorized,
  retryWaitSeconds,
  type ApiEnvelope,
} from '~/types/api'
import type { ApiFetchOptions } from '~/utils/apiFetch'
import { useGlobalLoading } from '~/composables/useGlobalLoading'
import { resolveClientPlatform } from '~/utils/clientPlatform'
import { resolveDeviceId } from '~/utils/deviceId'
import { resolveTenantHost, TENANT_HOST_HEADER } from '~/utils/tenantHost'
import { fundLockedMessage } from '~/utils/retryWait'
import { recordServerClock } from '~/utils/time'

type ResponseContext = FetchContext & { response: FetchResponse<unknown> }

/** 同源 API 前缀（apiFetch.baseURL 共用；Nitro `/api/**` 代理到后端） */
export const API_BASE_URL = '/api/v1'

/** 每个请求发出时的设备时刻（算往返耗时，挑最准的时钟样本） */
const requestSentAt = new WeakMap<FetchContext, number>()

/** 每个请求发出时携带的 Authorization（弱网下旧请求的 401 可能晚于新登录才返回） */
const requestAuth = new WeakMap<FetchContext, string>()

/** 每个请求是否计入了全局 loading（跳过标记的请求不计） */
const loadingCounted = new WeakMap<FetchContext, boolean>()

/** 请求开始时计入全局 loading；结束时对应减计数 */
function beginGlobalLoading(ctx: FetchContext) {
  if (!import.meta.client || readRequestOptions(ctx).skipGlobalLoading) return
  useGlobalLoading().begin()
  loadingCounted.set(ctx, true)
}

function endGlobalLoading(ctx: FetchContext) {
  if (!loadingCounted.delete(ctx)) return
  useGlobalLoading().end()
}

/**
 * 服务端时钟样本：所有响应（含错误响应）都带 X-Server-Time（规范 5.1-3），读不到时退回 Date 头。
 * 浏览器只打同源 /api（Nitro routeRules 代理或 nginx 直达 game-api，都会原样带回这两个头，
 * 同源下无需 CORS Expose）。只在客户端记：服务端渲染不格式化时间，也不跑倒计时。
 */
function observeServerClock(ctx: ResponseContext) {
  if (!import.meta.client) return
  const sentAt = requestSentAt.get(ctx)
  if (sentAt === undefined) return
  const headers = ctx.response.headers
  recordServerClock({
    serverTime: headers.get('X-Server-Time'),
    date: headers.get('Date'),
    sentAt,
    receivedAt: Date.now(),
  })
}

/** 前端 locale → 后端 lang（zh-Hans / zh-Hant / en） */
const API_LANG_MAP: Record<string, string> = {
  'zh-CN': 'zh-Hans',
  'zh-TW': 'zh-Hant',
  'zh-HK': 'zh-Hant',
  en: 'en',
}

function resolveApiLang(): string {
  try {
    const locale = String(useNuxtApp().$i18n.locale.value)
    return API_LANG_MAP[locale] ?? 'en'
  } catch {
    return 'en'
  }
}

function isEnvelope(value: unknown): value is ApiEnvelope {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as ApiEnvelope).code === 'number' &&
    ('data' in value || 'message' in value || 'msg' in value)
  )
}

/** 错误码 + 消息关键字 → 硬编码文案（覆盖后端原始错误消息） */
const ERROR_CODE_HARDCODED: Array<{
  code: number
  messageKeyword: string
  zh: string
  hant: string
  en: string
}> = [
  {
    code: 3001,
    messageKeyword: 'failed precondition: deposit in progress (confirming) cannot be cancelled',
    zh: '该订单支付已确认不可关闭',
    hant: '該訂單支付已確認不可關閉',
    en: 'The order payment has been confirmed and cannot be closed',
  },
]

function resolveFriendlyMessage(code: number, rawMessage: string): string {
  const entry = ERROR_CODE_HARDCODED.find(
    e => e.code === code && rawMessage.includes(e.messageKeyword),
  )
  if (!entry) return rawMessage
  try {
    const locale = String(useNuxtApp().$i18n.locale.value)
    if (locale === 'zh-TW' || locale === 'zh-HK') return entry.hant
    if (locale.startsWith('zh')) return entry.zh
    return entry.en
  } catch {
    return rawMessage
  }
}

/**
 * 带剩余等待时间的错误改用前端文案：服务端 1030 文案只说「请稍后再试」，
 * 这里按 data.retry_after_seconds > Retry-After > locked_until − 此刻 算出「请 1 小时 58 分后再试」。
 * 所有带资金密码的请求（收款账户增删改、撤销提现、改资金密码、资金密码校验）都经过这里。
 */
function resolveWaitMessage(code: number, data: unknown, headers?: Headers): string | null {
  if (code !== FUND_PASSWORD_LOCKED_CODE) return null
  try {
    return fundLockedMessage(useNuxtApp().$i18n.t, retryWaitSeconds(data, headers))
  } catch {
    return null
  }
}

function pickErrorMessage(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) return payload
  if (payload && typeof payload === 'object') {
    const { message, msg } = payload as { message?: string; msg?: string }
    if (message?.trim()) return message
    if (msg?.trim()) return msg
  }
  return fallback
}

function toastError(message: string) {
  if (!import.meta.client) return
  try {
    useNotify().error(message)
  } catch {
    // toast 未就绪时忽略
  }
}

function readRequestOptions(
  ctx: FetchContext,
): Pick<ApiFetchOptions, 'skipErrorToast' | 'skipAuth' | 'skipGlobalLoading'> {
  return ctx.options as ApiFetchOptions
}

function fail(
  ctx: FetchContext,
  message: string,
  options: { code: number; data?: unknown; status?: number },
): never {
  // 带上响应头：调用方据 Retry-After 显示真实剩余秒数（验证码冷却、锁定等）
  const headers = ctx.response?.headers
  const text = resolveWaitMessage(options.code, options.data, headers) ?? message
  if (!readRequestOptions(ctx).skipErrorToast) toastError(text)
  throw new ApiError(text, { ...options, headers })
}

/**
 * 会话探活：携带当前 token 的请求返回 401，可能是后端会话尚未全局生效
 * （弱网/副本同步延迟/时钟偏移），不能直接清登录态；用同一 token 间隔重探 /me，
 * 多次均 401 才认定会话确实失效。走裸 $fetch 绕过本拦截器（防递归与误清），
 * 按 token 去重：并发 401 共享同一次探活。
 */
let sessionProbe: { token: string; req: Promise<boolean> } | null = null

/** 探活确认窗口：最多 N 次、每次间隔；弱网下单次往返本身耗时，窗口随之拉长 */
const SESSION_PROBE_ATTEMPTS = 3
const SESSION_PROBE_RETRY_DELAY_MS = 1200

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

/** 日志里只留 token 尾 8 位，便于比对 sentAuth 与当前会话 */
const tokenTail = (auth: string) => (auth ? `…${auth.slice(-8)}` : '(none)')

/** 单次探活：仅 401（含业务壳 code=401）判定失效；网络错误 / 5xx 无法确认，视为仍有效 */
async function probeSessionOnce(token: string): Promise<boolean> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Client-Platform': resolveClientPlatform(),
    'X-Device-Id': resolveDeviceId(),
    Authorization: `Bearer ${token}`,
  }
  if (import.meta.server) {
    const host = resolveTenantHost()
    if (host) headers[TENANT_HOST_HEADER] = host
  }

  try {
    const payload = await $fetch(`${API_BASE_URL}/me`, {
      headers,
      query: { lang: resolveApiLang() },
    })
    return !(isEnvelope(payload) && payload.code === 401)
  } catch (err) {
    return (err as { response?: { status?: number } })?.response?.status !== 401
  }
}

export function probeSession(token: string): Promise<boolean> {
  if (sessionProbe?.token === token) {
    console.debug('[auth] joining in-flight session probe')
    return sessionProbe.req
  }

  const req = (async () => {
    for (let attempt = 1; attempt <= SESSION_PROBE_ATTEMPTS; attempt++) {
      const alive = await probeSessionOnce(token)
      console.info(
        `[auth] session probe ${attempt}/${SESSION_PROBE_ATTEMPTS}:`,
        alive ? 'alive' : 'still 401',
      )
      if (alive) return true
      // 重试间隔；期间会话被换掉（重新登录）则不再判定失效
      if (attempt < SESSION_PROBE_ATTEMPTS) {
        await sleep(SESSION_PROBE_RETRY_DELAY_MS)
        if (useUserStore().token !== token) return true
      }
    }
    return false
  })().finally(() => {
    if (sessionProbe?.req === req) sessionProbe = null
  })

  sessionProbe = { token, req }
  return req
}

async function rejectUnauthorized(
  ctx: FetchContext,
  status: number,
  payload: unknown,
): Promise<never> {
  const envelope = isEnvelope(payload) ? payload : undefined
  const errorOptions = {
    code: envelope?.code ?? status,
    data: envelope?.data ?? payload,
    status,
    headers: ctx.response?.headers,
  }

  if (!readRequestOptions(ctx).skipAuth) {
    const store = useUserStore()
    const sentAuth = requestAuth.get(ctx) ?? ''
    const currentToken = store.token
    console.info(
      '[auth] 401 received:',
      ctx.request,
      '| sentAuth:',
      tokenTail(sentAuth),
      '| currentToken:',
      tokenTail(currentToken),
    )
    // 请求发出时的 token 与当前会话不一致（登录前发出的旧请求）：属过期响应，不得清掉新会话
    if (currentToken && sentAuth !== `Bearer ${currentToken}`) {
      console.info('[auth] stale 401 suppressed (sentAuth != current token)')
      throw new ApiError('Unauthorized', errorOptions)
    }
    // 携带当前 token 的 401：先探活确认真失效才清会话；探活期间会话已被换掉（新登录）同样不清
    if (currentToken && sentAuth === `Bearer ${currentToken}`) {
      const alive = await probeSession(currentToken)
      console.info('[auth] 401 on current token, probe alive =', alive)
      if (alive || useUserStore().token !== currentToken) {
        throw new ApiError('Unauthorized', errorOptions)
      }
    }
    if (store.expireOnUnauthorized()) {
      console.warn('[auth] session cleared by confirmed 401:', ctx.request)
      throw new ApiError('Unauthorized', errorOptions)
    }
    console.info('[auth] 401 with no active session, nothing to clear:', ctx.request)
  }

  fail(ctx, pickErrorMessage(payload, 'Unauthorized'), errorOptions)
}

/**
 * 请求拦截：lang 查询参数、Accept、X-Client-Platform、X-Device-Id、Bearer token（走同源 /api，由 Nitro 代理）；
 * 服务端渲染时另带 X-Tenant-Host（原始域名，见 utils/tenantHost.ts）。
 *
 * 不再发 X-Api-Key（2026-10-01）：租户只按域名识别，同一份构建服务所有租户域名。
 */
export const onApiRequest: FetchHook = ctx => {
  requestSentAt.set(ctx, Date.now())
  beginGlobalLoading(ctx)
  const headers = new Headers(ctx.options.headers as HeadersInit | undefined)
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json')
  }

  if (import.meta.server && !headers.has(TENANT_HOST_HEADER)) {
    const host = resolveTenantHost()
    if (host) headers.set(TENANT_HOST_HEADER, host)
  }

  if (!headers.has('X-Client-Platform')) {
    headers.set('X-Client-Platform', resolveClientPlatform())
  }

  if (!headers.has('X-Device-Id')) {
    headers.set('X-Device-Id', resolveDeviceId())
  }

  const token = useUserStore().token
  if (!readRequestOptions(ctx).skipAuth && token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  requestAuth.set(ctx, headers.get('Authorization') ?? '')
  console.debug(
    '[auth] request:',
    ctx.options.method ?? 'GET',
    ctx.request,
    '| auth:',
    tokenTail(headers.get('Authorization') ?? ''),
    readRequestOptions(ctx).skipAuth ? '| skipAuth' : '',
  )
  ctx.options.headers = headers
  ctx.options.query = {
    ...(ctx.options.query as Record<string, unknown> | undefined),
    lang: resolveApiLang(),
  }
}

/**
 * 请求级失败（无响应：断网 / 超时 / 连接重置）：ofetch 不走 onResponseError，
 * 必须在这里收尾全局 loading，否则弱网下计数泄漏、loading 常驻。
 * 不触碰会话——网络不通不代表 token 失效。
 */
export const onApiRequestError: FetchHook = ctx => {
  endGlobalLoading(ctx)
  let message = 'Network error'
  try {
    message = useNuxtApp().$i18n.t('login.errorNetwork') || message
  } catch {
    /* i18n 上下文不可用时退回兜底文案 */
  }
  fail(ctx, message, { code: 0 })
}

/**
 * 响应拦截：
 * - 统一壳 { code, data }：成功则解包为 data；业务失败抛 ApiError
 * - 直接返回 DTO（无 code）的接口原样透传
 */
export const onApiResponse: FetchHook<ResponseContext> = async ctx => {
  endGlobalLoading(ctx)
  // 先记时钟样本：下面业务失败会直接抛出
  observeServerClock(ctx)
  const payload = ctx.response._data
  if (!isEnvelope(payload)) return

  if (API_SUCCESS_CODES.has(payload.code)) {
    ctx.response._data = payload.data
    return
  }

  if (isApiUnauthorized(ctx.response.status, payload.code)) {
    return rejectUnauthorized(ctx, ctx.response.status, payload)
  }

  const rawMessage = pickErrorMessage(payload, `Request failed: ${payload.code}`)
  fail(ctx, resolveFriendlyMessage(payload.code, rawMessage), {
    code: payload.code,
    data: payload.data,
    status: ctx.response.status,
  })
}

/** 响应错误拦截：401 需确认会话真失效才清登录态；客户端 toast 提示 */
export const onApiResponseError: FetchHook<ResponseContext> = async ctx => {
  endGlobalLoading(ctx)
  const { status, _data: data } = ctx.response
  const businessCode = isEnvelope(data) ? data.code : undefined

  if (isApiUnauthorized(status, businessCode)) {
    return rejectUnauthorized(ctx, status, data)
  }

  if (isEnvelope(data) && data.code >= 3000) {
    const rawMessage = pickErrorMessage(data, `Request failed: ${data.code}`)
    fail(ctx, resolveFriendlyMessage(data.code, rawMessage), {
      code: data.code,
      data: data.data,
      status,
    })
  }

  fail(ctx, pickErrorMessage(data, `Request failed${status ? ` (${status})` : ''}`), {
    code: status,
    data,
    status,
  })
}

/** 合并全局拦截器与单次请求传入的 hooks（全局优先执行） */
export function mergeHooks<T>(globalHook: T, local?: T | T[]): T[] {
  if (!local) return [globalHook]
  return Array.isArray(local) ? [globalHook, ...local] : [globalHook, local]
}
